import type { components } from "./schema.ts";

export type ApiSchemas = components["schemas"];
export type Session = ApiSchemas["Session"];
export type Device = ApiSchemas["Device"];
export type DevicePage = ApiSchemas["DevicePage"];
export type Organisation = ApiSchemas["Organisation"];
export type Department = ApiSchemas["Department"];
export type Recording = ApiSchemas["Recording"];
export type Problem = ApiSchemas["Problem"];
export type Display = ApiSchemas["Display"];
export type IceServer = ApiSchemas["IceServer"];

export function formatControlError(problem: Problem): string {
  const text = `${problem.status} ${problem.type} ${problem.title}`;
  return problem.detail ? `${text} ${problem.detail}` : text;
}

export class ControlError extends Error {
  readonly status: number;
  readonly problemType: string;

  constructor(problem: Problem) {
    super(formatControlError(problem));
    this.name = "ControlError";
    this.status = problem.status;
    this.problemType = problem.type;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function text(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function bool(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

function num(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function numOrNull(value: unknown): number | null | undefined {
  if (value === null) return null;
  return num(value);
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  if (typeof value !== "string") return undefined;
  for (const item of allowed) {
    if (item === value) return item;
  }
  return undefined;
}

const ACCESS = ["Attended", "Unattended"] as const;
const SESSION_STATE = ["AwaitingConsent", "Active", "Ended"] as const;
const CONSENT = ["NotRequired", "Pending", "Granted", "Denied", "TimedOut", "NoInteractiveUser"] as const;
const DISCONNECT = [
  "OperatorEnded",
  "AgentEnded",
  "ConsentDenied",
  "ConsentTimedOut",
  "NoInteractiveUser",
  "DeviceRevoked",
  "CredentialInvalid",
  "NetworkLost",
  "Error",
] as const;
const DEVICE_STATUS = ["Pending", "Enrolled", "Revoked", "Unknown"] as const;
const PRESENCE = ["Online", "Offline"] as const;
const ARCH = ["x64", "arm64"] as const;

function parseDisplay(value: unknown): Display | undefined {
  if (!isRecord(value) || !isRecord(value.bounds)) return undefined;
  const displayId = text(value.displayId);
  const name = text(value.name);
  const primary = bool(value.primary);
  const x = num(value.bounds.x);
  const y = num(value.bounds.y);
  const width = num(value.bounds.width);
  const height = num(value.bounds.height);
  const scaleFactor = num(value.scaleFactor);
  if (!displayId || name === undefined || primary === undefined) return undefined;
  if (x === undefined || y === undefined || width === undefined || height === undefined) return undefined;
  if (scaleFactor === undefined || scaleFactor <= 0 || width < 1 || height < 1) return undefined;
  return { displayId, name, primary, bounds: { x, y, width, height }, scaleFactor };
}

function parseIceServer(value: unknown): IceServer | undefined {
  if (!isRecord(value) || !Array.isArray(value.urls) || value.urls.length === 0) return undefined;
  const urls: string[] = [];
  for (const url of value.urls) {
    if (typeof url !== "string" || url.length === 0) return undefined;
    urls.push(url);
  }
  const server: IceServer = { urls };
  if (value.username !== undefined) {
    if (typeof value.username !== "string") return undefined;
    server.username = value.username;
  }
  if (value.credential !== undefined) {
    if (typeof value.credential !== "string") return undefined;
    server.credential = value.credential;
  }
  return server;
}

export function parseProblem(value: unknown, status: number): Problem {
  if (!isRecord(value)) {
    return { type: "urn:wylo-rc:error:unauthenticated", title: "Request failed", status };
  }
  const type = text(value.type) ?? "urn:wylo-rc:error:validation";
  const title = text(value.title) ?? "Request failed";
  const problemStatus = num(value.status) ?? status;
  const problem: Problem = { type, title, status: problemStatus };
  const detail = text(value.detail);
  if (detail) problem.detail = detail;
  return problem;
}

export function parseSession(value: unknown): Session {
  if (!isRecord(value) || !isRecord(value.consent) || !isRecord(value.features) || !isRecord(value.signalr)) {
    throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid session", status: 502 });
  }
  const sessionId = text(value.sessionId);
  const deviceId = text(value.deviceId);
  const accessMode = oneOf(value.accessMode, ACCESS);
  const state = oneOf(value.state, SESSION_STATE);
  const consentState = oneOf(value.consent.state, CONSENT);
  const clipboard = bool(value.features.clipboard);
  const fileTransfer = bool(value.features.fileTransfer);
  const recording = Object.hasOwn(value.features, "recording") ? bool(value.features.recording) : false;
  const turnEnabled = bool(value.turnEnabled);
  const hub = value.signalr.hub;
  const method = value.signalr.method;
  const signalUrl = text(value.signalr.url);
  const accessToken = text(value.signalr.accessToken);
  const group = text(value.signalr.group);
  if (!sessionId || !deviceId || !accessMode || !state || !consentState) {
    throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid session", status: 502 });
  }
  if (clipboard === undefined || fileTransfer === undefined || (Object.hasOwn(value.features, "recording") && recording === undefined) || turnEnabled === undefined) {
    throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid session", status: 502 });
  }
  if (
    hub !== "rc" ||
    method !== "signal" ||
    !signalUrl ||
    accessToken === undefined ||
    (accessToken.length === 0 && state !== "Ended") ||
    !group
  ) {
    throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid session", status: 502 });
  }
  if (!Array.isArray(value.iceServers)) {
    throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid session", status: 502 });
  }
  const iceServers: IceServer[] = [];
  for (const server of value.iceServers) {
    const parsed = parseIceServer(server);
    if (!parsed) throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid session", status: 502 });
    iceServers.push(parsed);
  }
  const session: Session = {
    sessionId,
    deviceId,
    accessMode,
    state,
    consent: { state: consentState },
    features: recording === undefined ? { clipboard, fileTransfer } : { clipboard, fileTransfer, recording },
    turnEnabled,
    iceServers,
    signalr: { url: signalUrl, accessToken, hub: "rc", method: "signal", group },
  };
  const deadlineAt = value.consent.deadlineAt;
  if (deadlineAt === null) session.consent.deadlineAt = null;
  if (typeof deadlineAt === "string") session.consent.deadlineAt = deadlineAt;
  const requestedAt = value.consent.requestedAt;
  if (requestedAt === null) session.consent.requestedAt = null;
  if (typeof requestedAt === "string") session.consent.requestedAt = requestedAt;
  const disconnectReason = oneOf(value.disconnectReason, DISCONNECT);
  if (disconnectReason) session.disconnectReason = disconnectReason;
  if (value.disconnectReason === null) session.disconnectReason = null;
  if (typeof value.sessionClaim === "string") session.sessionClaim = value.sessionClaim;
  return session;
}

const PLATFORMS = ["windows", "macos", "android", "ios"] as const;

function isPlatform(value: unknown): value is (typeof PLATFORMS)[number] {
  return typeof value === "string" && (PLATFORMS as readonly string[]).includes(value);
}

export function parseDevice(value: unknown): Device {
  if (!isRecord(value) || !isRecord(value.os)) {
    throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid device", status: 502 });
  }
  const deviceId = text(value.deviceId);
  const status = oneOf(value.status, DEVICE_STATUS);
  const presence = oneOf(value.presence, PRESENCE);
  const hostname = text(value.hostname);
  const name = text(value.name);
  const agentVersion = text(value.agentVersion);
  const platform = value.os.platform;
  const version = text(value.os.version);
  const architecture = oneOf(value.os.architecture, ARCH);
  const createdAt = text(value.createdAt);
  if (!deviceId || !status || !presence || hostname === undefined || !name || agentVersion === undefined) {
    throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid device", status: 502 });
  }
  if (!createdAt || Number.isNaN(Date.parse(createdAt))) {
    throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid device", status: 502 });
  }
  if (!isPlatform(platform) || !version || !architecture || !Array.isArray(value.displays) || !Array.isArray(value.tags)) {
    throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid device", status: 502 });
  }
  let capabilities: string[] | undefined;
  if (value.capabilities !== undefined) {
    if (!Array.isArray(value.capabilities) || value.capabilities.some((item) => typeof item !== "string")) {
      throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid device", status: 502 });
    }
    capabilities = value.capabilities;
  }
  const displays: Display[] = [];
  for (const display of value.displays) {
    const parsed = parseDisplay(display);
    if (!parsed) throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid device", status: 502 });
    displays.push(parsed);
  }
  const tags: Device["tags"] = [];
  for (const tag of value.tags) {
    if (!isRecord(tag) || typeof tag.key !== "string" || typeof tag.value !== "string" || tag.key.length === 0) {
      throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid device", status: 502 });
    }
    tags.push({ key: tag.key, value: tag.value });
  }
  const recordingSelectable = value.recordingSelectable === true;
  const recordingForced = value.recordingForced === true;
  const stealthCommand = value.stealthCommand === true;
  if (value.recordingSelectable !== undefined && value.recordingSelectable !== true && value.recordingSelectable !== false) {
    throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid device", status: 502 });
  }
  if (value.recordingForced !== undefined && value.recordingForced !== true && value.recordingForced !== false) {
    throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid device", status: 502 });
  }
  const device: Device = {
    deviceId,
    status,
    presence,
    hostname,
    name,
    agentVersion,
    createdAt,
    os: { platform, version, architecture },
    displays,
    tags,
    recordingSelectable,
    recordingForced,
    stealthCommand,
  };
  if (value.reasonRequired === true) device.reasonRequired = true;
  if (capabilities !== undefined) device.capabilities = capabilities;
  if (typeof value.protocolVersion === "string") device.protocolVersion = value.protocolVersion;
  if (typeof value.minimumAgentVersion === "string") device.minimumAgentVersion = value.minimumAgentVersion;
  if (typeof value.organisationId === "string") device.organisationId = value.organisationId;
  if (value.organisationId === null) device.organisationId = null;
  if (typeof value.departmentId === "string") device.departmentId = value.departmentId;
  if (value.departmentId === null) device.departmentId = null;
  device.lastHeartbeatAt = instant(value, "lastHeartbeatAt");
  device.lastBootAt = instant(value, "lastBootAt");
  if (!Object.hasOwn(value, "loggedOnUser") || value.loggedOnUser === null) {
    device.loggedOnUser = null;
  } else if (typeof value.loggedOnUser === "string") {
    device.loggedOnUser = value.loggedOnUser;
  } else {
    throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid device", status: 502 });
  }
  return device;
}

function instant(value: Record<string, unknown>, key: "lastHeartbeatAt" | "lastBootAt"): string | null {
  const raw = value[key];
  if (!Object.hasOwn(value, key) || raw === null) return null;
  if (typeof raw === "string" && !Number.isNaN(Date.parse(raw))) return raw;
  throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid device", status: 502 });
}

export type DeleteDeviceResult = { deviceId: string; endedSessions: number };

export function parseDeleteDevice(value: unknown): DeleteDeviceResult {
  const deviceId = isRecord(value) ? text(value.deviceId) : undefined;
  const endedSessions = isRecord(value) ? num(value.endedSessions) : undefined;
  if (deviceId === undefined || endedSessions === undefined || !Number.isInteger(endedSessions) || endedSessions < 0) {
    throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid delete result", status: 502 });
  }
  return { deviceId, endedSessions };
}

export function parseDevicePage(value: unknown): DevicePage {
  if (!isRecord(value) || !Array.isArray(value.items)) {
    throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid device list", status: 502 });
  }
  const page: DevicePage = { items: value.items.map((item) => parseDevice(item)) };
  if (typeof value.nextCursor === "string") page.nextCursor = value.nextCursor;
  if (value.nextCursor === null) page.nextCursor = null;
  return page;
}

export function parseOrganisations(value: unknown): Organisation[] {
  if (!Array.isArray(value)) {
    throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid organisation list", status: 502 });
  }
  return value.map((item) => {
    if (!isRecord(item) || typeof item.organisationId !== "string" || typeof item.name !== "string") {
      throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid organisation list", status: 502 });
    }
    return { organisationId: item.organisationId, name: item.name };
  });
}

export function parseDepartments(value: unknown): Department[] {
  if (!Array.isArray(value)) {
    throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid department list", status: 502 });
  }
  return value.map((item) => {
    if (!isRecord(item) || typeof item.departmentId !== "string" || typeof item.organisationId !== "string" || typeof item.name !== "string") {
      throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid department list", status: 502 });
    }
    return { departmentId: item.departmentId, organisationId: item.organisationId, name: item.name };
  });
}

export type RecordingPage = ApiSchemas["RecordingPage"];

export type WhoSource = "snapshot" | "directory";

export type RecordingAccessRow = {
  eventId: string;
  occurredAt: string;
  who: string;
  whoGuid: string | null;
  whoSource: WhoSource | null;
  deviceId: string | null;
  sessionId: string | null;
  intent: string;
};

export type RecordingAccessPage = {
  items: RecordingAccessRow[];
  nextCursor: string | null;
};

export function parseRecordings(value: unknown): Recording[] {
  if (!Array.isArray(value)) {
    throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid recording list", status: 502 });
  }
  return value.map((item) => parseRecording(item));
}

export function parseRecordingPage(value: unknown): RecordingPage {
  if (!isRecord(value) || !Array.isArray(value.items)) {
    throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid recording list", status: 502 });
  }
  const page: RecordingPage = { items: value.items.map((item) => parseRecording(item)) };
  if (typeof value.nextCursor === "string") page.nextCursor = value.nextCursor;
  if (value.nextCursor === null) page.nextCursor = null;
  return page;
}

function parseRecording(item: unknown): Recording {
  if (!isRecord(item) || typeof item.recordingId !== "string" || typeof item.sessionId !== "string") {
    throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid recording list", status: 502 });
  }
  if (typeof item.deviceId !== "string" || typeof item.createdAt !== "string") {
    throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid recording list", status: 502 });
  }
  if (typeof item.sha256 !== "string" || typeof item.bytes !== "number" || typeof item.retentionUntil !== "string") {
    throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid recording list", status: 502 });
  }
  if (typeof item.deviceName !== "string" || typeof item.connectedUser !== "string" || typeof item.startedAt !== "string") {
    throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid recording list", status: 502 });
  }
  const recording: Recording = {
    recordingId: item.recordingId,
    sessionId: item.sessionId,
    deviceId: item.deviceId,
    sha256: item.sha256,
    bytes: item.bytes,
    retentionUntil: item.retentionUntil,
    createdAt: item.createdAt,
    deviceName: item.deviceName,
    connectedUser: item.connectedUser,
    startedAt: item.startedAt,
  };
  if (typeof item.endedAt === "string" || item.endedAt === null) recording.endedAt = item.endedAt;
  if (typeof item.contentType === "string") recording.contentType = item.contentType;
  return recording;
}

export type AuditLogRow = {
  eventId: string;
  type: string;
  occurredAt: string;
  who: string;
  whoGuid: string | null;
  whoSource: WhoSource | null;
  deviceId: string | null;
  sessionId: string | null;
  detail: string;
  requestId?: string | null;
};

export const AUDIT_EVENT_TYPES = [
  "DeviceEnrolled",
  "DeviceApproved",
  "DeviceRevoked",
  "SessionStarted",
  "ConsentResult",
  "SessionEnded",
  "FeatureUsed",
  "FileTransfer",
  "RecordingAccessed",
  "EnrollmentMinted",
  "EnrollmentGenerationRevoked",
  "OrganisationCreated",
  "DepartmentCreated",
  "AgentInstallerIssued",
  "RecordingRuleChanged",
  "DirectoryUserChanged",
  "DirectoryRoleChanged",
  "DevicePlacementChanged",
  "DeviceTagsChanged",
  "StealthCommandPolicyChanged",
  "DeviceCapabilitiesChanged",
  "CapabilityDenied",
  "CapabilityReportRejected",
  "ReasonPolicyChanged",
  "RecordingProtectFailed",
  "DeviceDeleted",
  "Unknown",
  "cad.requested",
  "cad.sent",
  "cad.failed",
] as const;

const AUDIT_EVENT_TYPE_SET = new Set<string>(AUDIT_EVENT_TYPES);

export const UNKNOWN_EVENT_LABEL = "Unknown event";

const AUDIT_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const OCCURRED_AT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:Z|[+-]\d{2}:\d{2})$/;

let unknownAuditSerial = 0;

const CAD_LIST_TYPES = new Set(["cad.requested", "cad.sent", "cad.failed"]);

const FAILED_REASON = new Map<string, string>([
  ["not_shown", "secure screen did not appear"],
  ["no_session", "no Windows session to send to"],
  ["policy", "Windows policy blocked it"],
  ["not_windows", "not a Windows session"],
  ["no_token", "could not prepare the session"],
  ["impersonate", "could not prepare the session"],
  ["call", "the call failed"],
]);

export type AgentRelease = {
  published: boolean;
  version: string | null;
};

export type AgentVersions = {
  windows: AgentRelease;
  macos: AgentRelease;
};

export type AuditLogPage = {
  items: AuditLogRow[];
  nextCursor: string | null;
};

function invalidAuditList(): ControlError {
  return new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid audit list", status: 502 });
}

function actorKindOf(value: unknown): "Operator" | "Device" | null {
  return value === "Operator" || value === "Device" ? value : null;
}

function requestedDetail(action: unknown): string {
  if (action === "timed_out") return "no device result";
  if (action === "requested" || action === undefined || action === null) return "requested";
  return "unknown";
}

function sentDetail(evidence: unknown): string {
  if (evidence === "name") return "device read the secure desktop as shown";
  if (evidence === "denied" || evidence === undefined || evidence === null) {
    return "device reports shown (inferred / basis unknown)";
  }
  return "unknown";
}

function failedDetail(reason: unknown): string {
  if (typeof reason !== "string") return "unknown";
  return FAILED_REASON.get(reason) ?? "unknown";
}

export function cadLatenessLabel(deviceAt: string, requestAt: string): string | null {
  const deviceMs = Date.parse(deviceAt);
  const requestMs = Date.parse(requestAt);
  if (!Number.isFinite(deviceMs) || !Number.isFinite(requestMs) || deviceMs < requestMs) return null;
  return `${Math.round((deviceMs - requestMs) / 1000)} s after the request`;
}

function parseCadListItem(item: Record<string, unknown>): AuditLogRow {
  if (typeof item.who !== "string" || typeof item.when !== "string" || typeof item.requestId !== "string") {
    throw invalidAuditList();
  }
  if (item.type !== "cad.requested" && item.type !== "cad.sent" && item.type !== "cad.failed") {
    throw invalidAuditList();
  }
  const actorKind = actorKindOf(item.actorKind);
  if (item.actorKind !== undefined && item.actorKind !== null && actorKind === null) throw invalidAuditList();
  const deviceAct = actorKind === "Device" && (item.type === "cad.sent" || item.type === "cad.failed");
  const identity = deviceAct
    ? { who: "Device", whoGuid: null, whoSource: null }
    : item.type === "cad.requested" && actorKind !== "Device"
      ? auditIdentity({
          kind: "Operator",
          operatorOid: item.who,
          operatorEmail: item.operatorEmail,
          operatorUpn: item.operatorUpn,
          operatorUpnSource: item.operatorUpnSource,
          operatorDirectoryName: item.operatorDirectoryName,
        })
      : { who: "unknown", whoGuid: null, whoSource: null };
  const detail =
    item.type === "cad.requested"
      ? actorKind === "Device"
        ? "unknown"
        : requestedDetail(item.action)
      : item.type === "cad.sent"
        ? actorKind === "Device"
          ? sentDetail(item.evidence)
          : "unknown"
        : actorKind === "Device"
          ? failedDetail(item.reason)
          : "unknown";
  return {
    eventId: `${item.type}:${item.requestId}`,
    type: item.type,
    occurredAt: item.when,
    who: identity.who,
    whoSource: identity.whoSource,
    whoGuid: item.type === "cad.requested" && identity.whoGuid && !identity.whoGuid.includes("@") ? identity.whoGuid : null,
    deviceId: typeof item.deviceId === "string" ? item.deviceId : null,
    sessionId: typeof item.sessionId === "string" ? item.sessionId : null,
    detail,
    requestId: item.requestId,
  };
}

const RECORDING_PROTECT_REASON = new Map<string, string>([
  ["handle_invalid", "Recording file not protected: file handle invalid"],
  ["read_denied", "Recording file not protected: permissions could not be read"],
  ["verify_error", "Recording file not protected: permissions check failed"],
  ["owner_untrusted", "Recording file not protected: owner is not SYSTEM or Administrators"],
  ["dacl_missing", "Recording file not protected: no access list"],
  ["dacl_unexpected", "Recording file not protected: access list allows other accounts"],
]);

function recordingProtectDetail(item: Record<string, unknown>): string {
  const token = typeof item.action === "string" ? item.action : "";
  const base = RECORDING_PROTECT_REASON.get(token) ?? "Recording file not protected: unknown reason";
  const count = item.denialCount;
  if (typeof count === "number" && Number.isInteger(count) && count >= 1 && count <= 65535) {
    return `${base} (Windows error ${count})`;
  }
  return base;
}

function auditDetail(item: Record<string, unknown>): string {
  if (item.type === "RecordingProtectFailed") {
    return recordingProtectDetail(item);
  }
  const parts: string[] = [];
  for (const key of ["recordingIntent", "feature", "fileName", "fileDirection", "consentState", "disconnectReason", "sessionReason"] as const) {
    const value = item[key];
    if (typeof value === "string" && value.length > 0) parts.push(value);
  }
  if (parts.length > 0) return parts.join(" ");
  return typeof item.type === "string" && item.type.length > 0 ? item.type : "Unknown";
}

function filled(value: string | null | undefined): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function userLine(fields: {
  upn?: string | null;
  directoryName?: string | null;
  displayName?: string | null;
  objectId?: string | null;
}): string {
  return filled(fields.upn) ?? filled(fields.directoryName) ?? filled(fields.displayName) ?? filled(fields.objectId) ?? "";
}

function whoSourceOf(value: unknown): WhoSource | null {
  return value === "snapshot" || value === "directory" ? value : null;
}

const IDENTITY_FALLBACK = /^[A-Za-z0-9._-]{1,32}$/;

function identityFallback(kind: string): string {
  return IDENTITY_FALLBACK.test(kind) ? kind : "operator";
}

function auditIdentity(actor: Record<string, unknown>): { who: string; whoGuid: string | null; whoSource: WhoSource | null } {
  const kind = typeof actor.kind === "string" ? actor.kind : "";
  if (kind === "System" || kind === "Device") return { who: kind, whoGuid: null, whoSource: null };
  const oid = typeof actor.operatorOid === "string" ? actor.operatorOid : "";
  const upn = typeof actor.operatorUpn === "string" ? actor.operatorUpn : "";
  const email = typeof actor.operatorEmail === "string" ? actor.operatorEmail : "";
  const directoryName = typeof actor.operatorDirectoryName === "string" ? actor.operatorDirectoryName : "";
  const whoSource = whoSourceOf(actor.operatorUpnSource);
  const who = whoSource === "snapshot"
    ? userLine({ upn, objectId: oid })
    : whoSource === "directory"
      ? userLine({ directoryName: directoryName || email, objectId: oid })
      : userLine({ upn, directoryName: email, objectId: oid });
  return { who: who.length > 0 ? who : identityFallback(kind), whoGuid: oid.length > 0 ? oid : null, whoSource };
}

export function parseAgentVersions(value: unknown): AgentVersions {
  if (!isRecord(value)) {
    throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid agent versions", status: 502 });
  }
  return { windows: agentRelease(value.windows), macos: agentRelease(value.macos) };
}

function agentRelease(value: unknown): AgentRelease {
  if (!isRecord(value) || typeof value.published !== "boolean") {
    throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid agent versions", status: 502 });
  }
  return { published: value.published, version: typeof value.version === "string" && value.version.length > 0 ? value.version : null };
}

function uuidOrNull(value: unknown): string | null {
  return typeof value === "string" && AUDIT_UUID.test(value) ? value : null;
}

function occurredAtOf(value: unknown): string {
  if (typeof value !== "string" || value.length > 40 || !OCCURRED_AT.test(value)) return "unknown";
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return "unknown";
  return new Date(ms).toISOString();
}

function unknownEventId(): string {
  unknownAuditSerial += 1;
  return `unknown:${unknownAuditSerial}`;
}

function standardEnvelope(item: Record<string, unknown>): boolean {
  return typeof item.eventId === "string" && typeof item.occurredAt === "string" && isRecord(item.actor);
}

function unknownAuditRow(item: Record<string, unknown>, occurredAt: unknown): AuditLogRow {
  return {
    eventId: unknownEventId(),
    type: "unknown",
    occurredAt: occurredAtOf(occurredAt),
    who: "unknown",
    whoGuid: null,
    whoSource: null,
    deviceId: uuidOrNull(item.deviceId),
    sessionId: uuidOrNull(item.sessionId),
    detail: "unknown",
  };
}

export function parseAuditLog(value: unknown): AuditLogPage {
  if (!isRecord(value) || !Array.isArray(value.items)) {
    throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid audit list", status: 502 });
  }
  const items: AuditLogRow[] = [];
  for (const item of value.items) {
    if (!isRecord(item) || typeof item.type !== "string") throw invalidAuditList();
    if (CAD_LIST_TYPES.has(item.type)) {
      items.push(parseCadListItem(item));
      continue;
    }
    if (item.type === "Unknown") {
      if (!standardEnvelope(item)) throw invalidAuditList();
      items.push(unknownAuditRow(item, item.occurredAt));
      continue;
    }
    if (AUDIT_EVENT_TYPE_SET.has(item.type)) {
      if (!standardEnvelope(item)) throw invalidAuditList();
      const identity = auditIdentity(item.actor as Record<string, unknown>);
      items.push({
        eventId: item.eventId as string,
        type: item.type,
        occurredAt: item.occurredAt as string,
        who: identity.who,
        whoGuid: identity.whoGuid,
        whoSource: identity.whoSource,
        deviceId: typeof item.deviceId === "string" ? item.deviceId : null,
        sessionId: typeof item.sessionId === "string" ? item.sessionId : null,
        detail: auditDetail(item),
      });
      continue;
    }
    if (standardEnvelope(item)) {
      items.push(unknownAuditRow(item, item.occurredAt));
      continue;
    }
    if (typeof item.who === "string" && typeof item.when === "string" && typeof item.requestId === "string") {
      items.push(unknownAuditRow(item, item.when));
      continue;
    }
    throw invalidAuditList();
  }
  const page: AuditLogPage = { items, nextCursor: null };
  if (typeof value.nextCursor === "string") page.nextCursor = value.nextCursor;
  if (value.nextCursor === null) page.nextCursor = null;
  return page;
}

export function parseRecordingAccess(value: unknown): RecordingAccessPage {
  if (!isRecord(value) || !Array.isArray(value.items)) {
    throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid audit list", status: 502 });
  }
  const items: RecordingAccessRow[] = [];
  for (const item of value.items) {
    if (!isRecord(item) || typeof item.eventId !== "string" || typeof item.occurredAt !== "string" || !isRecord(item.actor)) {
      throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid audit list", status: 502 });
    }
    if (item.type !== "RecordingAccessed") continue;
    const identity = auditIdentity(item.actor);
    items.push({
      eventId: item.eventId,
      occurredAt: item.occurredAt,
      who: identity.who,
      whoGuid: identity.whoGuid,
      whoSource: identity.whoSource,
      deviceId: typeof item.deviceId === "string" ? item.deviceId : null,
      sessionId: typeof item.sessionId === "string" ? item.sessionId : null,
      intent: typeof item.recordingIntent === "string" ? item.recordingIntent : "Unknown",
    });
  }
  const page: RecordingAccessPage = { items, nextCursor: null };
  if (typeof value.nextCursor === "string") page.nextCursor = value.nextCursor;
  return page;
}

export type QualitySample = {
  sampledAt: string;
  fps: number;
  bitrate: number;
  rttMs: number | null;
  jitterMs: number | null;
  packetLoss: number | null;
  captureMs: number;
  encodeMs: number;
  icePath: "direct" | "relayed";
};

export type SessionQuality = {
  offerLagMs: number | null;
  summary: string;
  samples: QualitySample[];
};

export function parseQuality(value: unknown): SessionQuality {
  if (!isRecord(value) || !Array.isArray(value.samples)) {
    throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid quality", status: 502 });
  }
  const offerLagMs = value.offerLagMs === null ? null : num(value.offerLagMs);
  const summary = text(value.summary);
  if (offerLagMs === undefined || !summary) {
    throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid quality", status: 502 });
  }
  const samples: QualitySample[] = [];
  for (const row of value.samples) {
    if (!isRecord(row)) {
      throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid quality", status: 502 });
    }
    const sampledAt = text(row.sampledAt);
    const fps = num(row.fps);
    const bitrate = num(row.bitrate);
    const rttMs = numOrNull(row.rttMs);
    const jitterMs = numOrNull(row.jitterMs);
    const packetLoss = numOrNull(row.packetLoss);
    const captureMs = num(row.captureMs);
    const encodeMs = num(row.encodeMs);
    const icePath = row.icePath === "direct" || row.icePath === "relayed" ? row.icePath : undefined;
    if (
      !sampledAt ||
      fps === undefined ||
      bitrate === undefined ||
      rttMs === undefined ||
      jitterMs === undefined ||
      packetLoss === undefined ||
      captureMs === undefined ||
      encodeMs === undefined ||
      !icePath
    ) {
      throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid quality", status: 502 });
    }
    samples.push({ sampledAt, fps, bitrate, rttMs, jitterMs, packetLoss, captureMs, encodeMs, icePath });
  }
  return { offerLagMs, summary, samples };
}
